/**
 * ThunderstormWarningLayer
 *
 * Displays BOM severe thunderstorm warning polygons on the 2D Leaflet map.
 * Color-coded by warning type with pulsing animation for dry thunderstorms (fire risk).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';

interface ThunderstormWarning {
  id: string;
  type: string;
  warningType: 'severe_thunderstorm' | 'severe_thunderstorm_hail' |
               'severe_thunderstorm_wind' | 'severe_thunderstorm_tornado' |
               'dry_thunderstorm';
  headline: string;
  description: string;
  areaDescription: string;
  urgency: string;
  severity: string;
  issued: string;
  expires: string | null;
}

interface ThunderstormWarningLayerProps {
  enabled: boolean;
  onWarningsLoaded?: (count: number) => void;
}

const WARNING_COLORS: Record<string, { fill: string; stroke: string }> = {
  dry_thunderstorm: { fill: '#dc2626', stroke: '#991b1b' },           // Red - fire risk
  severe_thunderstorm_tornado: { fill: '#7c3aed', stroke: '#5b21b6' }, // Purple
  severe_thunderstorm_hail: { fill: '#2563eb', stroke: '#1d4ed8' },    // Blue
  severe_thunderstorm_wind: { fill: '#f97316', stroke: '#ea580c' },    // Orange
  severe_thunderstorm: { fill: '#eab308', stroke: '#ca8a04' }          // Yellow
};

const THUNDERSTORM_URL = import.meta.env.VITE_THUNDERSTORM_URL ||
  `${import.meta.env.VITE_STATIC_URL || 'https://d3c2yw8hurho5b.cloudfront.net'}/thunderstorm/warnings.geojson`;

export const ThunderstormWarningLayer: React.FC<ThunderstormWarningLayerProps> = ({
  enabled,
  onWarningsLoaded
}) => {
  const [geojsonData, setGeojsonData] = useState<GeoJSON.FeatureCollection | null>(null);
  useMap(); // Hook ensures component re-renders on map changes

  const fetchWarnings = useCallback(async () => {
    if (!enabled) return;

    try {
      const response = await fetch(THUNDERSTORM_URL);
      if (!response.ok) {
        console.warn('No thunderstorm warnings available');
        setGeojsonData(null);
        onWarningsLoaded?.(0);
        return;
      }

      const data = await response.json();
      setGeojsonData(data);
      onWarningsLoaded?.(data.features?.length || 0);

    } catch (error) {
      console.warn('Error fetching thunderstorm warnings:', error);
      setGeojsonData(null);
      onWarningsLoaded?.(0);
    }
  }, [enabled, onWarningsLoaded]);

  useEffect(() => {
    if (!enabled) {
      setGeojsonData(null);
      return;
    }

    fetchWarnings();

    // Refresh every 5 minutes
    const interval = setInterval(fetchWarnings, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [enabled, fetchWarnings]);

  if (!enabled || !geojsonData || !geojsonData.features?.length) return null;

  const style = (feature: GeoJSON.Feature | undefined): L.PathOptions => {
    if (!feature) return {};
    const warningType = feature.properties?.warningType || 'severe_thunderstorm';
    const colors = WARNING_COLORS[warningType] || WARNING_COLORS.severe_thunderstorm;

    return {
      fillColor: colors.fill,
      fillOpacity: 0.25,
      color: colors.stroke,
      weight: 2,
      opacity: 0.8,
      dashArray: warningType === 'dry_thunderstorm' ? '5, 5' : undefined,
      className: warningType === 'dry_thunderstorm' ? 'thunderstorm-pulse' : undefined
    };
  };

  const onEachFeature = (feature: GeoJSON.Feature, layer: L.Layer) => {
    const props = feature.properties as ThunderstormWarning;
    const colors = WARNING_COLORS[props.warningType] || WARNING_COLORS.severe_thunderstorm;

    const isDryThunderstorm = props.warningType === 'dry_thunderstorm';
    const icon = isDryThunderstorm ? '<span style="font-size: 18px;">&#9889;&#128293;</span>' : '<span style="font-size: 18px;">&#9928;&#65039;</span>';

    const popup = `
      <div class="thunderstorm-popup" style="max-width: 280px;">
        <h3 style="margin: 0 0 8px; color: ${colors.stroke}; display: flex; align-items: center; gap: 6px;">
          ${icon}
          <span>${props.headline || 'Thunderstorm Warning'}</span>
        </h3>
        <p style="margin: 0 0 8px; font-size: 12px; color: #6b7280;">
          ${props.areaDescription || ''}
        </p>
        <p style="margin: 0 0 8px; font-size: 13px; line-height: 1.4;">
          ${(props.description || '').substring(0, 250)}${(props.description || '').length > 250 ? '...' : ''}
        </p>
        ${isDryThunderstorm ? `
          <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; padding: 8px; margin-bottom: 8px;">
            <strong style="color: #dc2626; font-size: 12px;">&#9888; Fire Ignition Risk</strong>
            <p style="margin: 4px 0 0; font-size: 11px; color: #7f1d1d;">
              Dry thunderstorms can ignite fires. Lightning with little or no rain reaching the ground.
            </p>
          </div>
        ` : ''}
        <div style="font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 8px;">
          <div><strong>Issued:</strong> ${props.issued ? new Date(props.issued).toLocaleString() : 'Unknown'}</div>
          ${props.expires ? `<div><strong>Expires:</strong> ${new Date(props.expires).toLocaleString()}</div>` : ''}
          <div style="margin-top: 4px;">Source: Bureau of Meteorology</div>
        </div>
      </div>
    `;

    layer.bindPopup(popup, { maxWidth: 300 });
  };

  // Use a stable key based on feature count and first feature ID
  const stableKey = `thunderstorm-${geojsonData.features.length}-${geojsonData.features[0]?.properties?.id || 'default'}`;

  return (
    <GeoJSON
      key={stableKey}
      data={geojsonData}
      style={style}
      onEachFeature={onEachFeature}
    />
  );
};

export default ThunderstormWarningLayer;
